import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import CheckoutButton from '../components-library/buttons/CheckoutButton';
import Footer from '../components-library/footers/Footer';
import OngoingOrder from '../components-library/order/OngoingOrder';
import { ORDER_METHODS, SCREENS } from '../services/exports/Constants';
import { OrderMethodModalProps } from '../services/exports/Interfaces';
import AddressHelpers from '../services/helpers/AddressHelpers';
import useScreenType from '../hooks/utility/useScreenType';
import CookieConsent from '../components-library/CookieConsent';
import CompanyDetailsModal from '../modals/CompanyDetailsModal';
import { useTranslation } from 'react-i18next';
import useInitialData from '../hooks/global/useInitialData';
import useAppState from '../hooks/global/useAppState';
import useRouter from '../hooks/navigation/useRouter';
import MenuCategoryList from '../components-library/menu/category-list/MenuCategoryList';
import PageLayout from '../components-library/layouts/PageLayout';
import useAppMethods from '../hooks/utility/useAppMethods';
import PoweredByFooter from '../components-library/footers/PoweredByFooter';
import DealsCarousel from '../components-library/carousel/deals/DealsCarousel';
import useOrderFlow from '../hooks/order/useOrderFlow';
import classnames from 'classnames';
import useDeepRef from '../hooks/utility/useDeepRef';
import useStorefront from '../hooks/storefront/useStorefront';
import OrderMethod from '../modules/storefront/components/orders/OrderMethod';
import CashbackProgramBanner from '../components-library/cashback/CashbackProgramBanner';
import MostPopularProductsCarousel from '../components-library/carousel/most-popular-products';
import AppliedOfferToast from '../features/offers/components/AppliedOfferToast';
import useIsOrderSlotValidWatcher from '../hooks/availability/useIsOrderSlotValidWatcher';
import useDeals from '../hooks/deals/useDeals';
import { MenuCategoryListProvider } from '../components-library/menu/category-list/MenuCategoryListProvider';
import MenuCategorySidebar from '../components-library/menu/MenuNavigationBar/MenuCategorySidebar';
import PicturesSlider from '../components-library/company/PicturesSlider';
import useOrderMethodModalGate from '../hooks/order/useOrderMethodModalGate';

export default function RestaurantDetailsScreen() {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:RestaurantDetailsScreen' });

  const { data } = useInitialData();
  const { company, franchise, loading } = data;
  const { isValidSlot, isLoading: isOrderSlotValidating } = useIsOrderSlotValidWatcher();
  const { isDesktop } = useScreenType();
  const { toggleOrderMethodModal, toggleMenuItemModal } = useAppMethods();
  const { orderMethodModalProps, menuItemModalProps } = useAppState();
  const { navigate } = useRouter();
  const { isStorefront } = useStorefront();
  const { hasImages: hasDealsWithImages } = useDeals();
  const { isOrderMethodInfoComplete, ongoingOrder, localData } = useOrderFlow();
  const needsDeliveryAddressInput =
    localData?.method === ORDER_METHODS.DELIVERY && !AddressHelpers.isDeliveryComplete(localData);
  const orderMethodModalPropsRef = useDeepRef<OrderMethodModalProps>(orderMethodModalProps);
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState(false);
  const [loadingNavigation, setLoadingNavigation] = useState(false);
  const { wasDismissed } = useOrderMethodModalGate({
    watchBasketReopen: true,
    holdReopen: !!menuItemModalProps?.openModal,
  });

  const menuListRef = useRef(null);

  useEffect(() => {
    if (
      loading ||
      isOrderSlotValidating ||
      orderMethodModalPropsRef.current.openModal ||
      (isOrderMethodInfoComplete && isValidSlot) ||
      !company?.has_live_orders ||
      wasDismissed
    ) {
      return;
    }

    if (!franchise?.has_multiple_companies && isValidSlot && !needsDeliveryAddressInput) {
      return;
    }

    const timeout = setTimeout(() => toggleOrderMethodModal({ openModal: true }), 4000);

    return () => clearTimeout(timeout);
  }, [
    loading,
    isOrderMethodInfoComplete,
    company?.has_live_orders,
    franchise?.has_multiple_companies,
    isValidSlot,
    isOrderSlotValidating,
    needsDeliveryAddressInput,
    wasDismissed,
  ]);

  const banner = useMemo(
    () =>
      Boolean(ongoingOrder?.id) && (
        <OngoingOrder className={classnames({ 'border-b': isStorefront || hasDealsWithImages })} />
      ),
    [ongoingOrder?.id, isStorefront, hasDealsWithImages],
  );

  const checkoutButton = useMemo<ReactElement | undefined>(() => {
    return (
      !orderMethodModalProps?.openModal &&
      !menuItemModalProps?.openModal &&
      !isDesktop && (
        <div className="lg:hidden block fixed bottom-0 w-full z-50 space-y-md px-md">
          <AppliedOfferToast />
          <CheckoutButton
            className="bg-transparent border-t-none !mb-4 !p-0"
            title={t('checkout_button.title')}
            onClick={() => {
              setLoadingNavigation(true);
              setTimeout(() => {
                navigate({ web: { to: SCREENS.BASKET } });
              }, 500);
            }}
            loading={loadingNavigation}
            hideAddProductsButton
          />
        </div>
      )
    );
  }, [orderMethodModalProps, isDesktop, menuItemModalProps, loadingNavigation]);

  return (
    <PageLayout className="!shadow-none bg-gray-background">
      <div>
        <main className="w-full" data-test="restaurant-details-body">
          <div>{banner}</div>
          <div
            className={classnames('space-y-sm lg:space-y-md pt-sm lg:pt-md', {
              'layout-container': isDesktop,
            })}
          >
            {(company?.show_hero_image_with_offers || !hasDealsWithImages) && (company?.pictures?.length ?? 0) > 0 && (
              <div className="px-md lg:px-none">
                <div className={heroSliderCard}>
                  <div className={heroSliderImage}>
                    <PicturesSlider pictures={company.pictures} className="overflow-hidden h-full w-full" />
                  </div>
                </div>
              </div>
            )}
            <OrderMethod className="px-md lg:px-none" />
            {hasDealsWithImages ? (
              <DealsCarousel.Image className="space-y-sm lg:space-y-md" />
            ) : (
              <DealsCarousel.Classic className="space-y-sm lg:space-y-md" />
            )}
            <MostPopularProductsCarousel
              className="space-y-sm lg:space-y-md"
              toggleProduct={(product) => toggleMenuItemModal({ openModal: true, product })}
            />
            <CashbackProgramBanner className="mx-md lg:mx-none" />
          </div>
          <MenuCategoryListProvider>
            <div
              className={classnames('flex flex-row gap-lg', {
                'layout-container': isDesktop,
              })}
            >
              <MenuCategorySidebar />
              <div className="relative flex-1 min-w-0 py-md">
                <div className="pr-none lg:pr-md">
                  <MenuCategoryList ref={menuListRef} skipProvider ListFooterComponent={<Footer />} />
                </div>
              </div>
            </div>
          </MenuCategoryListProvider>
          {checkoutButton}
          <PoweredByFooter />
        </main>
        <CompanyDetailsModal
          data={company}
          openModal={showCompanyDetailsModal}
          toggleModal={() => setShowCompanyDetailsModal(!showCompanyDetailsModal)}
        />
        <CookieConsent />
      </div>
    </PageLayout>
  );
}

const heroSliderCard = 'relative rounded-2xl overflow-hidden border border-brand-border bg-brand-card';
const heroSliderImage = 'h-44 sm:h-56 md:h-60 lg:h-64 w-full overflow-hidden';
